/**
 * Domain status → intent registries.
 *
 * This is the DRY linchpin of the color system. Every product status enum
 * maps here, in one file. To re-tune any status color across every sub-app:
 * edit one entry below. No file in `products/` ever names "emerald" or
 * "cobalt" — it names a status key, and the registry resolves it through
 * the intent module.
 *
 * When you add a new domain enum:
 *   1. Import the type from the contract that owns it.
 *   2. Add a `<DOMAIN>_STATUS_INTENT: Record<Status, Intent>` constant.
 *   3. The styleguide page auto-renders the new domain on next reload.
 *
 * Type imports are kept at the contract level so we get compile-time errors
 * if a status is added or renamed upstream — the registry must stay
 * exhaustive.
 */

import type { Intent } from "./intents";
import type { TaskStatus, TaskPriority } from "../types/projects";
import type {
  ApplicationStatus,
} from "../api/contracts/hiring-applications";
import type { StageKind } from "../api/contracts/hiring-pipelines";
import type { JobShape } from "../api/contracts/hiring-jobs";
import type { z } from "zod";
import type { overallRating } from "../api/contracts/hiring-scorecards";
import type { IntakeBatchStatus } from "../api/contracts/intake";

// ─── Excellent: tasks ──────────────────────────────────────────────────

export const TASK_STATUS_INTENT: Record<TaskStatus, Intent> = {
  backlog: "neutral",
  todo: "info",
  in_progress: "warning",
  review: "info",
  done: "success",
  cancelled: "neutral",
};

export const TASK_PRIORITY_INTENT: Record<TaskPriority, Intent> = {
  low: "neutral",
  medium: "info",
  high: "warning",
  urgent: "danger",
};

// ─── Hiring: jobs ──────────────────────────────────────────────────────

type JobStatus = JobShape["status"];

export const JOB_STATUS_INTENT: Record<JobStatus, Intent> = {
  draft: "neutral",
  open: "success",
  closed: "neutral",
  archived: "neutral",
};

// ─── Hiring: applications (per-record status) ─────────────────────────

export const APPLICATION_STATUS_INTENT: Record<ApplicationStatus, Intent> = {
  active: "info",
  hired: "success",
  rejected: "danger",
  withdrawn: "neutral",
};

// ─── Hiring: pipeline stages (kind classification) ────────────────────

export const HIRING_STAGE_INTENT: Record<StageKind, Intent> = {
  lead: "neutral",
  active: "info",
  offer: "warning",
  hired: "success",
  rejected: "danger",
};

// ─── Hiring: scorecard overall rating ──────────────────────────────────

type ScorecardRating = z.infer<typeof overallRating>;

export const SCORECARD_RATING_INTENT: Record<ScorecardRating, Intent> = {
  strong_yes: "success",
  yes: "success",
  no: "warning",
  strong_no: "danger",
};

// ─── Document: generation jobs ─────────────────────────────────────────

export type DocJobStatus = "pending" | "running" | "done" | "error";

export const DOC_JOB_INTENT: Record<DocJobStatus, Intent> = {
  pending: "neutral",
  running: "warning",
  done: "success",
  error: "danger",
};

// ─── Agents: worker runs ───────────────────────────────────────────────
// Matches `excellent_worker_runs.status` CHECK constraint in migrations.ts.

export type WorkerRunStatus =
  | "running"
  | "completed"
  | "failed"
  | "budget_exceeded"
  | "aborted";

export const WORKER_RUN_STATUS_INTENT: Record<WorkerRunStatus, Intent> = {
  running: "info",
  completed: "success",
  failed: "danger",
  budget_exceeded: "warning",
  aborted: "neutral",
};

// ─── Agents: goals ─────────────────────────────────────────────────────
// Matches `excellent_goals.status` CHECK constraint.

export type GoalStatus = "active" | "paused" | "done" | "abandoned";

export const GOAL_STATUS_INTENT: Record<GoalStatus, Intent> = {
  active: "info",
  paused: "warning",
  done: "success",
  abandoned: "neutral",
};

// ─── Excellent: verification runs (auto-verify) ───────────────────────
// Matches `verification_runs.status` CHECK constraint.

export type VerificationRunStatus =
  | "running"
  | "completed"
  | "error"
  | "timeout"
  | "superseded";

export const VERIFICATION_RUN_STATUS_INTENT: Record<
  VerificationRunStatus,
  Intent
> = {
  running: "info",
  completed: "success",
  error: "danger",
  timeout: "danger",
  superseded: "neutral",
};

// Checking-waterfall discharge tier (§3.1). T0–T2 are model-free proof;
// T3/T4 are model-judged and must not appear unless that tier ran.
export type VerificationTierStatus =
  | "t0"
  | "t1"
  | "t2"
  | "t3"
  | "t4"
  | "undischarged";

export const VERIFICATION_TIER_INTENT: Record<VerificationTierStatus, Intent> = {
  t0: "success",
  t1: "success",
  t2: "success",
  t3: "warning",
  t4: "warning",
  undischarged: "neutral",
};

// ─── Optimization Harness ("Crucible") ────────────────────────────────
// Matches excellent_loops / _loop_variants / _promotions CHECK constraints
// + the verdict (human gate) taxonomy. UI-SPEC §0. "muted"/"muted-success"/
// "destructive" in the spec map to the five canonical intents below.

export type LoopStatus = "draft" | "active" | "paused" | "retired";
export const LOOP_STATUS_INTENT: Record<LoopStatus, Intent> = {
  draft: "neutral",
  active: "success",
  paused: "warning",
  retired: "neutral",
};

export type LoopVariantStatus =
  | "candidate" | "shadow" | "canary" | "active"
  | "last_good" | "rolled_back" | "retired";
export const LOOP_VARIANT_STATUS_INTENT: Record<LoopVariantStatus, Intent> = {
  candidate: "neutral",
  shadow: "info",
  canary: "warning",
  active: "success",
  last_good: "success", // muted-success
  rolled_back: "danger",
  retired: "neutral",
};

export type PromotionStatus =
  | "proposed" | "shadow" | "active" | "demoted" | "rolled_back";
export const PROMOTION_STATUS_INTENT: Record<PromotionStatus, Intent> = {
  proposed: "neutral",
  shadow: "info",
  active: "success",
  demoted: "warning",
  rolled_back: "danger",
};

export type VerdictStatus = "pending" | "accepted" | "rejected" | "edited";
export const VERDICT_STATUS_INTENT: Record<VerdictStatus, Intent> = {
  pending: "warning",
  accepted: "success",
  rejected: "danger",
  edited: "info",
};

// ─── Universal Intake: batch ledger ────────────────────────────────────
// Matches `intake_batches.status` CHECK (v120) + INTAKE-PRD §2.8.2:
// landed/undone neutral · classified info · awaiting_* warning ·
// materialized success · failed error(→danger). Call sites name the status,
// never the color.

export const INTAKE_STATUS_INTENT: Record<IntakeBatchStatus, Intent> = {
  landed: "neutral",
  classified: "info",
  awaiting_confirm: "warning",
  awaiting_answer: "warning",
  materialized: "success",
  undone: "neutral",
  failed: "danger",
};

// ─── CRM: sequence enrollments ─────────────────────────────────────────
// Matches `sequence_enrollments.status` CHECK constraint.

export type SequenceEnrollmentStatus =
  | "active"
  | "paused"
  | "completed"
  | "cancelled"
  | "failed";

export const SEQUENCE_ENROLLMENT_STATUS_INTENT: Record<
  SequenceEnrollmentStatus,
  Intent
> = {
  active: "info",
  paused: "warning",
  completed: "success",
  cancelled: "neutral",
  failed: "danger",
};

// ─── CRM: opportunity stages (dynamic, flag-driven) ──────────────────

/**
 * CRM pipeline stages are user-defined per workspace, so we can't map
 * by enum. Instead we derive intent from the stage's flags + position.
 *
 * Rules:
 *   - is_won = success
 *   - is_lost = danger
 *   - probability_pct >= 75 = warning (close to close)
 *   - probability_pct >= 25 = info  (in-flight)
 *   - default = neutral
 *
 * Call sites pass the stage row directly; this is the single place where
 * the heuristic lives.
 */
export function opportunityStageIntent(stage: {
  is_won: number;
  is_lost: number;
  probability_pct: number;
}): Intent {
  if (stage.is_won) return "success";
  if (stage.is_lost) return "danger";
  if (stage.probability_pct >= 75) return "warning";
  if (stage.probability_pct >= 25) return "info";
  return "neutral";
}

// ─── Registry map ──────────────────────────────────────────────────────

/**
 * Top-level domain map. Keyed for the StatusBadge/StatusDot blocks so
 * they can resolve `domain="task" status="in_progress"` to the right
 * intent without a giant switch at the call site.
 *
 * Domains with dynamic (non-enum) status (e.g. crm opportunity stages)
 * are NOT included here — those call sites use the dedicated helper
 * (`opportunityStageIntent`) directly.
 */
export const DOMAIN_STATUS_INTENT = {
  task: TASK_STATUS_INTENT,
  task_priority: TASK_PRIORITY_INTENT,
  job: JOB_STATUS_INTENT,
  application: APPLICATION_STATUS_INTENT,
  hiring_stage: HIRING_STAGE_INTENT,
  scorecard_rating: SCORECARD_RATING_INTENT,
  doc_job: DOC_JOB_INTENT,
  worker_run: WORKER_RUN_STATUS_INTENT,
  goal: GOAL_STATUS_INTENT,
  verification_run: VERIFICATION_RUN_STATUS_INTENT,
  verification_tier: VERIFICATION_TIER_INTENT,
  sequence_enrollment: SEQUENCE_ENROLLMENT_STATUS_INTENT,
  loop: LOOP_STATUS_INTENT,
  loop_variant: LOOP_VARIANT_STATUS_INTENT,
  promotion: PROMOTION_STATUS_INTENT,
  verdict: VERDICT_STATUS_INTENT,
  intake: INTAKE_STATUS_INTENT,
} as const;

export type StatusDomain = keyof typeof DOMAIN_STATUS_INTENT;

export type StatusOf<D extends StatusDomain> =
  keyof (typeof DOMAIN_STATUS_INTENT)[D];

// ─── Deterministic intent from a free-form slug/label ──────────────────

/**
 * Hash a string to one of the five intents. Stable across reloads and
 * across the codebase — same input always produces the same intent.
 *
 * Used by:
 *   - `sectionIntent(slug)` to color user-defined sections
 *   - CRM lead tables to color user-defined lead statuses
 *   - any other place where the set of values is open-ended but you still
 *     want every value to read as a distinct colored pill
 *
 * Cheap sum-of-charcodes hash. Deterministic, no allocations, dependency-
 * free; collision rate is fine for the small N (5 buckets).
 */
const INTENT_BUCKETS: readonly Intent[] = [
  "info",
  "success",
  "warning",
  "danger",
  "neutral",
] as const;

export function hashIntent(key: string): Intent {
  if (!key) return "neutral";
  let sum = 0;
  for (let i = 0; i < key.length; i++) sum = (sum + key.charCodeAt(i)) | 0;
  return INTENT_BUCKETS[Math.abs(sum) % INTENT_BUCKETS.length]!;
}

// ─── Commitment: the canonical lifecycle (cross-kind) ──────────────────

/**
 * The one generalized commitment lifecycle (Object Model §8): every kind's
 * per-domain state machine is a refinement of these five phases. Mapping each
 * kind onto a phase is what lets ONE status system color Task, Deal, Goal,
 * Application, Run, Lead consistently — and lets the Queue reason about
 * "what's stalled in review" across all kinds at once.
 *
 *   proposed  = neutral   (backlog / todo / applied / queued / stage 1)
 *   active    = info      (in_progress / running / mid-pipeline)
 *   review    = warning   (awaiting verify / scorecard / negotiation)
 *   fulfilled = success   (done / won / hired / completed)
 *   cancelled = danger    (lost / rejected / withdrawn / abandoned / failed)
 *
 * This is the canonical-phase complement to `commitmentStateIntent`
 * (commitment-table.tsx), which keyword-matches a free-form state directly to
 * an intent. Use `lifecycleIntent` when you want the state collapsed to its
 * canonical phase first (e.g. a `KindBoard` column header, a cross-kind Queue).
 */
export const LIFECYCLE_PHASES = [
  "proposed",
  "active",
  "review",
  "fulfilled",
  "cancelled",
] as const;
export type LifecyclePhase = (typeof LIFECYCLE_PHASES)[number];

export const LIFECYCLE_INTENT: Record<LifecyclePhase, Intent> = {
  proposed: "neutral",
  active: "info",
  review: "warning",
  fulfilled: "success",
  cancelled: "danger",
};

/**
 * Collapse a free-form per-kind state onto its canonical lifecycle phase.
 * Keyword-driven so it works for any kind's vocabulary (and any C15 Lexicon
 * relabeling) without an enum per kind. Order matters: terminal-negative and
 * terminal-positive are checked before the in-flight buckets.
 */
export function lifecyclePhase(state: string | null | undefined): LifecyclePhase {
  if (!state) return "proposed";
  const s = state.toLowerCase();
  if (/(cancel|abandon|lost|reject|withdraw|breach|fail|declin)/.test(s))
    return "cancelled";
  if (/(fulfill|done|complete|won|hired|closed[_ -]?won|deliver|ship)/.test(s))
    return "fulfilled";
  if (/(review|verify|verif|scorecard|negotiat|approval|pending[_ -]?review)/.test(s))
    return "review";
  if (/(active|progress|running|in[_ -]?flight|screen|interview|qualif|engag)/.test(s))
    return "active";
  if (/(proposed|backlog|todo|draft|new|applied|queued|unqualif|lead)/.test(s))
    return "proposed";
  return "active";
}

/** Map a free-form per-kind state to an intent via its canonical phase. */
export function lifecycleIntent(state: string | null | undefined): Intent {
  return LIFECYCLE_INTENT[lifecyclePhase(state)];
}

// ─── Excellent: sections ──────────────────────────────────────────────

/**
 * Section slug → intent. The five built-in sections get pinned colors so
 * the most common shell pages have stable, meaningful tints; every other
 * (user-created) section falls through to `hashIntent` for a deterministic
 * choice. Sections have no `color` column in the DB — this map is the
 * entire color system for sections.
 */
const SECTION_INTENT_OVERRIDES: Record<string, Intent> = {
  operations: "info",
  design: "neutral",
  hiring: "success",
  development: "warning",
  marketing: "danger",
};

export function sectionIntent(slug: string | null | undefined): Intent {
  if (!slug) return "neutral";
  const normalized = slug.toLowerCase();
  return SECTION_INTENT_OVERRIDES[normalized] ?? hashIntent(normalized);
}
